import React from 'react';

interface PrivacyPolicyPageProps {
  onBack: () => void;
}

const PrivacyPolicyPage: React.FC<PrivacyPolicyPageProps> = ({ onBack }) => {
  const lastUpdated = "9 January 2025";

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center text-primary-600 hover:text-primary-700 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-bold text-midnight">Privacy Policy</h1>
          <p className="text-gray-600 mt-2">Last updated: {lastUpdated}</p>
        </div>

        <div className="bg-white rounded-xl shadow-soft p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              AI Marker Mate is committed to protecting the privacy and security of personal information. 
              This Privacy Policy explains how we collect, use, disclose, and protect information when 
              schools and teachers use our AI-powered essay marking service.
            </p>
            <p className="text-gray-700 leading-relaxed">
              This policy complies with the Australian Privacy Principles (APPs) under the Privacy Act 1988 (Cth) 
              and applies to our services provided to Australian educational institutions.
            </p>
          </section>

          {/* What We Collect */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">What Information We Collect</h2>
            
            <h3 className="text-lg font-medium text-midnight mb-3">Student Assessment Data</h3>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside">
              <li>Student essays and written responses</li>
              <li>Rubric selections and marking criteria</li>
              <li>Assessment scores and feedback</li>
              <li>Teacher comments and annotations</li>
              <li>Timestamps and submission metadata</li>
              <li>Student identifiers (preferably student numbers, not full names)</li>
            </ul>
            
            <h3 className="text-lg font-medium text-midnight mb-3">Account and Technical Information</h3>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside">
              <li>Teacher account information and email addresses</li>
              <li>School or institution identifiers</li>
              <li>Device and application metadata</li>
              <li>Usage logs and system performance data</li>
            </ul>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
              <p className="text-amber-800 text-sm">
                <strong>Important:</strong> Even de-identified data can become personal information if it can be 
                re-identified when considered in context with other available information. We implement safeguards 
                to minimize this risk.
              </p>
            </div>
          </section>

          {/* Why We Collect Data */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Why We Collect This Information</h2>
            
            <h3 className="text-lg font-medium text-midnight mb-3">Core Service Delivery</h3>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside">
              <li>Automated essay marking and grading</li>
              <li>Generating detailed feedback for students</li>
              <li>Supporting teacher moderation and quality assurance</li>
              <li>Maintaining audit trails for assessment integrity</li>
              <li>Providing analytics and insights to educators</li>
            </ul>

            <h3 className="text-lg font-medium text-midnight mb-3">Optional Product Improvement</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              With the school's prior written consent, we may use de-identified copies of student essays, 
              rubric selections, and system logs to develop and evaluate features such as scoring quality 
              and feedback clarity. We remove direct identifiers and apply technical and organisational 
              controls to reduce the risk of re-identification. These datasets are stored separately from 
              production systems, retained for up to 24 months, and deleted or further de-identified after 
              that period. Schools can withdraw consent at any time, in which case we stop using new data 
              and delete or de-identify previously collected data unless we are legally required to retain it.
            </p>
          </section>

          {/* Children's Data */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Children's Data Protection</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our service is designed for use by schools and teachers, not for children to sign up directly. 
              Consent for the processing of student data is obtained from and managed by the educational 
              institution under their authority as part of their educational services.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              We recognize the particular sensitivity of children's personal information and implement 
              additional safeguards including data minimization, purpose limitation, and enhanced security measures.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We monitor developments in privacy regulation, including the OAIC's upcoming Children's Online 
              Privacy Code, and commit to compliance with all applicable requirements.
            </p>
          </section>

          {/* Data Minimization */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Data Minimisation and PII Protection</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Essays should not include personal information such as full names, addresses, photos, or any 
              details that could reasonably identify a student. Teachers should use student numbers or other 
              anonymised identifiers. We apply automated checks to detect likely PII and may block, redact, 
              or delete content that appears to contain PII. If we discover uploaded PII, we will work with 
              the school to remove it or replace it with a de-identified version.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Our safeguards include automated PII detection algorithms, teacher review processes before upload, 
              and regular audits of stored content to identify and remediate any inadvertent PII inclusion.
            </p>
          </section>

          {/* Third Party Processors */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Third-Party Processors and AI Vendors</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We work with carefully selected third-party service providers to deliver our services:
            </p>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside">
              <li><strong>Cloud Hosting:</strong> Google Cloud Platform (Firebase) with data stored in australia-southeast1</li>
              <li><strong>AI Inference:</strong> Google Gemini API (paid enterprise services), OpenAI API, and Anthropic Claude API</li>
              <li><strong>Analytics:</strong> Minimal usage analytics for service improvement</li>
              <li><strong>Email Services:</strong> For system notifications and support communications</li>
              <li><strong>Payment Processing:</strong> Stripe (when applicable, card data not stored by us)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              For AI services specifically, we use paid enterprise APIs where prompts and responses are not 
              used to improve the AI providers' products, though some data may be retained temporarily for 
              abuse monitoring. We maintain agreements with all processors requiring them to meet Australian 
              privacy standards equivalent to the APPs.
            </p>
          </section>

          {/* Data Residency */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Data Residency and Cross-Border Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Primary data storage occurs in Australia via Google Cloud Platform's australia-southeast1 region. 
              However, some data processing may occur overseas through our AI service providers:
            </p>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside">
              <li><strong>United States:</strong> Google AI services, OpenAI, and Anthropic processing</li>
              <li><strong>European Union:</strong> Backup storage and content delivery networks</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Under APP 8, we remain accountable for overseas handling and take reasonable steps to ensure 
              overseas recipients meet privacy protection standards substantially similar to the APPs.
            </p>
          </section>

          {/* Security Measures */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Security Measures</h2>
            <p className="text-gray-700 leading-relaxed mb-4">We implement comprehensive security measures including:</p>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside">
              <li>Encryption in transit (TLS 1.3) and at rest (AES-256)</li>
              <li>Role-based access controls and least privilege principles</li>
              <li>Multi-factor authentication for administrative access</li>
              <li>Comprehensive audit logging and monitoring</li>
              <li>Regular security assessments and penetration testing</li>
              <li>Staff privacy and security training programs</li>
              <li>Vendor due diligence and security requirement agreements</li>
              <li>Regular reviews of security policies and procedures</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Personal information is de-identified or securely destroyed when no longer needed for the 
              purposes for which it was collected, where appropriate and legally permissible.
            </p>
          </section>

          {/* Retention and Deletion */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Data Retention and Deletion</h2>
            <p className="text-gray-700 leading-relaxed mb-4">Our data retention practices include:</p>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside">
              <li><strong>Student essays and assessments:</strong> Retained for 12 months by default for quality assurance and moderation, then deleted or de-identified</li>
              <li><strong>Account information:</strong> Retained while the account remains active, plus 24 months after closure</li>
              <li><strong>System logs:</strong> Retained for 6 months for security and performance monitoring</li>
              <li><strong>Training datasets:</strong> When consent is provided, retained for up to 24 months then deleted or further de-identified</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Schools can request earlier deletion of their data at any time by contacting our privacy team. 
              Some data may be retained longer where required by law or for legitimate business purposes 
              such as dispute resolution.
            </p>
          </section>

          {/* Access and Correction */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Access and Correction Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Consistent with APPs 12 and 13, individuals have rights to request access to and correction of 
              their personal information. For student data, these requests should be made through the educational 
              institution, which will coordinate with us to provide access or make corrections as appropriate.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Teachers can access and update their account information directly through our platform. 
              For other access or correction requests, contact us at nick.hull.42@gmail.com.
            </p>
          </section>

          {/* Data Breach Response */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Data Breach Response</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              In the event of a suspected data breach, we follow a structured response process:
            </p>
            <ol className="text-gray-700 leading-relaxed mb-4 space-y-2 list-decimal list-inside">
              <li>Immediate containment and assessment of the incident</li>
              <li>Determination of whether there is likely risk of serious harm</li>
              <li>If required, notification to the OAIC as soon as practicable (within 30 days where possible)</li>
              <li>Notification to affected schools and individuals where there is likely risk of serious harm</li>
              <li>Implementation of remediation measures and monitoring</li>
            </ol>
            <p className="text-gray-700 leading-relaxed">
              For incident reports or security concerns, contact us immediately at nick.hull.42@gmail.com 
              with "SECURITY INCIDENT" in the subject line.
            </p>
          </section>

          {/* Cookies and Analytics */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Cookies and Analytics</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our web application uses minimal cookies and local storage for:
            </p>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside">
              <li>Authentication and session management (essential)</li>
              <li>User preferences and application state (functional)</li>
              <li>Basic usage analytics to improve service performance (optional)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              You can disable non-essential cookies through your browser settings. This may affect some 
              functionality but will not prevent use of core features.
            </p>
          </section>

          {/* Payments */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Payment Processing</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              When payment features are available, credit card data is processed securely by Stripe, 
              our payment processor. We do not store credit card information on our systems. 
              Stripe's privacy policy governs the handling of payment data: 
              <a href="https://stripe.com/privacy" className="text-primary-600 hover:underline ml-1" target="_blank" rel="noopener noreferrer">
                https://stripe.com/privacy
              </a>
            </p>
          </section>

          {/* Contact and Complaints */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Contact Us and Privacy Complaints</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For privacy-related inquiries, access requests, or complaints, contact us at:
            </p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-gray-700"><strong>Email:</strong> nick.hull.42@gmail.com</p>
              <p className="text-gray-700"><strong>Subject Line:</strong> Privacy Inquiry - AI Marker Mate</p>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              We aim to acknowledge privacy complaints within 5 business days and resolve them within 30 days. 
              If you are not satisfied with our response, you may lodge a complaint with the Office of the 
              Australian Information Commissioner (OAIC) at www.oaic.gov.au.
            </p>
          </section>

          {/* Policy Changes */}
          <section>
            <h2 className="text-xl font-semibold text-midnight mb-4">Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may update this privacy policy from time to time to reflect changes in our practices, 
              technology, legal requirements, or other factors. When we make material changes, we will:
            </p>
            <ul className="text-gray-700 leading-relaxed mb-4 space-y-2 list-disc list-inside">
              <li>Update the "Last Updated" date at the top of this policy</li>
              <li>Notify schools and users via email or in-app notification</li>
              <li>For significant changes, provide 30 days' notice where reasonably practicable</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              Continued use of our services after policy updates constitutes acceptance of the revised terms.
            </p>
          </section>

          {/* Footer Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
            <p className="text-blue-800 text-sm">
              <strong>This policy was last updated on {lastUpdated}.</strong> For questions about this policy 
              or our privacy practices, please contact us at nick.hull.42@gmail.com.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
